import { enableProdMode } from '@angular/core'
import { bootstrapApplication } from '@angular/platform-browser'
import * as Sentry from '@sentry/angular'

import { environment } from './environments/environment'
import { appConfig } from './app/app.config'
import { AppComponent } from './app/app.component'
import { configureZod, SENTRY_IGNORE_ERRORS, tagApiError } from '@seiri/shared/frontend'

const { production, buildCommit, buildCommitTime, name, sentry, sentryDsn, apiHost } = environment

if (production) {
  enableProdMode()
}

// Zod's messages are shown under form fields (Signal Forms `validateStandardSchema`), so they must be
// in the product's language: Zod's own locale for its built-in issues, VALIDATION_SK for Seiri's
// rules. The backend keeps English. See docs/api-errors.md.
configureZod()

Sentry.init({
  dsn: sentryDsn,
  environment: name,
  release: buildCommit,
  enabled: sentry,
  // Third-party errors we cannot fix; each entry says where it comes from.
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration(),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    Sentry.replayIntegration(),
  ],

  // 100% tracing burns Sentry quota for no diagnostic gain once the sample is representative.
  tracesSampleRate: production ? 0.1 : 1.0,

  // Which URLs get sentry-trace/baggage headers, so a browser trace links to the backend trace
  // for the same operation. This was left on the copy-paste default (yourserver.io), which meant
  // trace headers never reached the real API and the two halves never joined up.
  tracePropagationTargets: ['localhost', ...(apiHost ? [apiHost] : []), /^\/api\//],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Tags an ApiError's event with its reqId, the join key to the backend log lines.
  beforeSend: tagApiError,
})

// Deliberate boot banner, printed before Angular (and therefore LoggerService) exists. It is the
// fastest way to confirm which build a browser actually loaded.
// eslint-disable-next-line no-console
console.info(
  `App Version: ${buildCommit} created at ${buildCommitTime ? new Date(buildCommitTime).toLocaleString() : '-'}`,
)

setTimeout(() => {
  bootstrapApplication(AppComponent, appConfig)
})
